import { catchError, defer, from, merge, Observable, shareReplay, startWith, Subject, switchMap, tap } from 'rxjs';

import { Injectable } from '@angular/core';
import { User } from '@shared/models';

import { ApiService } from '../api/api.service';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  #updateSubject = new Subject<boolean>();
  #updatedUser$ = this.#updateSubject.asObservable().pipe(
    startWith(true),
    switchMap(() => this.#getUser()),
  );
  loadUserFromStorage$: Observable<User | null> = defer(() => this.#loadUserFromStorage());
  user$: Observable<User | null> = merge(this.loadUserFromStorage$, this.#updatedUser$).pipe(
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  constructor(
    private storageService: StorageService,
    private apiService: ApiService,
  ) {}

  updateUser(): void {
    this.#updateSubject.next(true);
  }

  async setUser(user: User | null): Promise<void> {
    await this.storageService.set('user', user);
  }

  #getUser(): Observable<User | null> {
    return this.apiService.get<User>('user').pipe(
      tap((user) => {
        this.setUser(user);
      }),
      catchError(() => {
        return from(this.#loadUserFromStorage());
      }),
    );
  }

  async #loadUserFromStorage(): Promise<User | null> {
    return await this.storageService.get<User>('user');
  }
}
